import {
  NATIONAL_STATE_CODE,
  type DBStateProductCount,
} from '../db/schema/stateProductCounts';

export { NATIONAL_STATE_CODE };

export interface StateProductCountEntry {
  state: string;
  planYear: number;
  maOnly: number;
  maPd: number;
  maSub: number;
  pdp: number;
  medSup: number;
  total: number;
}

export const STATE_PRODUCT_COUNT_CSV_HEADERS = [
  'Year',
  'State',
  'MA-only',
  'MA-PD',
  'MA sub',
  'PDP',
  'Total',
] as const;

/** Per-state editable fields (excludes nationwide Med Sup). */
export type StateProductCountField =
  | 'maOnly'
  | 'maPd'
  | 'maSub'
  | 'pdp'
  | 'total';

export const STATE_PRODUCT_COUNT_FIELDS: {
  field: StateProductCountField;
  label: string;
}[] = [
  { field: 'maOnly', label: 'MA-only' },
  { field: 'maPd', label: 'MA-PD' },
  { field: 'maSub', label: 'MA sub' },
  { field: 'pdp', label: 'PDP' },
  { field: 'total', label: 'Total' },
];

/** Default nationwide Med Sup product count (treated as a single product). */
export const DEFAULT_NATIONAL_MED_SUP_COUNT = 1;

const HEADER_ALIASES: Record<string, StateProductCountField> = {
  'ma-only': 'maOnly',
  'ma only': 'maOnly',
  maonly: 'maOnly',
  'ma-pd': 'maPd',
  mapd: 'maPd',
  'ma sub': 'maSub',
  masub: 'maSub',
  pdp: 'pdp',
  total: 'total',
};

export function normalizeStateProductCountState(raw: string): string {
  const trimmed = raw.trim().toUpperCase();
  if (trimmed === 'NATIONAL' || trimmed === 'US') {
    return NATIONAL_STATE_CODE;
  }
  if (!/^[A-Z]{2}$/.test(trimmed)) {
    throw new Error(`Invalid state code: ${raw}`);
  }
  return trimmed;
}

export function formatStateProductCountState(state: string): string {
  return state === NATIONAL_STATE_CODE ? 'NATIONAL' : state;
}

export function parseNonNegativeCount(value: string): number | 'invalid' {
  const trimmed = value.trim();
  if (trimmed === '') return 0;
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 0) {
    return 'invalid';
  }
  return parsed;
}

function parseCount(value: string, label: string, state: string): number {
  const parsed = parseNonNegativeCount(value);
  if (parsed === 'invalid') {
    throw new Error(`Invalid count for ${label} in ${state}: ${value}`);
  }
  return parsed;
}

function normalizeHeader(header: string): string {
  return header.trim().toLowerCase().replace(/\s+/g, ' ');
}

function resolveCountHeader(
  headers: string[]
): Record<StateProductCountField, string> | null {
  const mapping = {} as Record<StateProductCountField, string>;
  for (const header of headers) {
    const normalized = normalizeHeader(header);
    if (normalized === 'state') continue;
    const field = HEADER_ALIASES[normalized];
    if (field) mapping[field] = header;
  }

  const required: StateProductCountField[] = [
    'maOnly',
    'maPd',
    'maSub',
    'pdp',
    'total',
  ];
  if (required.every((field) => mapping[field])) {
    return mapping;
  }
  return null;
}

function parseSimpleCsvRecords(csv: string): Record<string, string>[] {
  const lines = csv
    .trim()
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    throw new Error('CSV must include a header row and at least one data row');
  }

  const headers = lines[0].split(',').map((header) => header.trim());
  return lines.slice(1).map((line, index) => {
    const values = line.split(',').map((value) => value.trim());
    if (values.length !== headers.length) {
      throw new Error(
        `CSV row ${index + 2} has ${values.length} columns; expected ${
          headers.length
        }`
      );
    }

    const row: Record<string, string> = {};
    headers.forEach((header, columnIndex) => {
      row[header] = values[columnIndex] ?? '';
    });
    return row;
  });
}

export function parseStateProductCountsCsv(
  csv: string,
  defaultPlanYear: number
): StateProductCountEntry[] {
  const rows = parseSimpleCsvRecords(csv);

  const headers = Object.keys(rows[0] ?? {});
  const stateHeader = headers.find((h) => normalizeHeader(h) === 'state');
  const yearHeader = headers.find((h) => normalizeHeader(h) === 'year');
  if (!stateHeader) {
    throw new Error('CSV must include a State column');
  }

  const countHeaders = resolveCountHeader(headers);
  if (!countHeaders) {
    throw new Error(
      'CSV must include MA-only, MA-PD, MA sub, PDP, and Total columns'
    );
  }

  const seen = new Set<string>();
  const entries: StateProductCountEntry[] = [];

  for (const row of rows) {
    const state = normalizeStateProductCountState(row[stateHeader] ?? '');
    const planYear = yearHeader
      ? parseCount(row[yearHeader] ?? '', 'Year', state)
      : defaultPlanYear;
    const key = `${planYear}:${state}`;
    if (seen.has(key)) {
      throw new Error(
        `Duplicate state row for ${planYear}: ${formatStateProductCountState(
          state
        )}`
      );
    }
    seen.add(key);

    const stateLabel = formatStateProductCountState(state);
    entries.push({
      state,
      planYear,
      maOnly: parseCount(row[countHeaders.maOnly] ?? '', 'MA-only', stateLabel),
      maPd: parseCount(row[countHeaders.maPd] ?? '', 'MA-PD', stateLabel),
      maSub: parseCount(row[countHeaders.maSub] ?? '', 'MA sub', stateLabel),
      pdp: parseCount(row[countHeaders.pdp] ?? '', 'PDP', stateLabel),
      // Med Sup is nationwide; CSV import preserves the existing national value.
      medSup: 0,
      total: parseCount(row[countHeaders.total] ?? '', 'Total', stateLabel),
    });
  }

  return entries.sort((a, b) => a.state.localeCompare(b.state));
}

export function formatStateProductCountsCsv(
  entries: StateProductCountEntry[]
): string {
  const sorted = [...entries].sort((a, b) => {
    if (a.state === NATIONAL_STATE_CODE) return 1;
    if (b.state === NATIONAL_STATE_CODE) return -1;
    return a.state.localeCompare(b.state);
  });

  const lines = [
    STATE_PRODUCT_COUNT_CSV_HEADERS.join(','),
    ...sorted.map((entry) =>
      [
        entry.planYear,
        formatStateProductCountState(entry.state),
        entry.maOnly,
        entry.maPd,
        entry.maSub,
        entry.pdp,
        entry.total,
      ].join(',')
    ),
  ];
  return lines.join('\n');
}

export function mapDbStateProductCount(
  row: DBStateProductCount
): StateProductCountEntry {
  return {
    state: row.state,
    planYear: row.plan_year,
    maOnly: row.ma_only,
    maPd: row.ma_pd,
    maSub: row.ma_sub,
    pdp: row.pdp,
    medSup: row.med_sup,
    total: row.total,
  };
}

export function getNationalMedSupCount(
  entries: Iterable<StateProductCountEntry>
): number {
  for (const entry of entries) {
    if (entry.state === NATIONAL_STATE_CODE) {
      return entry.medSup;
    }
  }
  return DEFAULT_NATIONAL_MED_SUP_COUNT;
}
